import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, TextField, Button, Typography,
  Alert, Grid, FormControl, InputLabel, Select, MenuItem,
  FormHelperText, Divider, IconButton, Tooltip
} from '@mui/material';
import { Print, Home, ArrowBack } from '@mui/icons-material';
import axios from 'axios';
import { fmt, sym, locale } from '../utils/currency';
import { useLearnerInvoices } from '../hooks/useLearnerInvoices';
import { useGLAccounts } from '../hooks/useGLAccounts';
import { glAccountOptions } from '../utils/glAccountOptions';
import { useBankAccounts } from '../hooks/useBankAccounts';
import CompanyHeader from './CompanyHeader';
import ReportFooter from './ReportFooter';

const PaymentReceiptForm = () => {
  const { state } = useLocation();
  const navigate = useNavigate();

  // ── Learner ───────────────────────────────────────
  const [learnerAdmNo, setLearnerAdmNo] = useState(state?.learnerAdmNo || '');
  const { invoices, loading: invoicesLoading } = useLearnerInvoices(learnerAdmNo);

  // ── Form fields ───────────────────────────────────
  const [selectedInvoiceNo, setSelectedInvoiceNo] = useState('');
  const [amount, setAmount] = useState('');
  const [paymentDate, setPaymentDate] = useState(
    new Date().toISOString().split('T')[0]
  );

  // **Bank dropdown**
  const { banks, loading: banksLoading } = useBankAccounts();
  const [selectedBank, setSelectedBank] = useState('');          // <-- NEW

  const [reference, setReference] = useState('');
  const [transactionDescription, setTransactionDescription] = useState('');

  // **GL dropdown**
  const { accounts: glAccounts, loading: glLoading } = useGLAccounts();
  const [selectedGLAccount, setSelectedGLAccount] = useState(''); // <-- NEW

  const [createdReceipt, setCreatedReceipt] = useState(null);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const glLabel = (no) => {
    const a = glAccounts.find(g => g.accountNo === no);
    return a ? `${a.accountNo} — ${a.accountName}` : no || '—';
  };
  const bankLabel = (no) => {
    const b = banks.find(b => b.accountNo === no);
    return b ? `${b.bankName} — ${b.accountName}` : no || '—';
  };
  const handlePrint = () => window.print();

  // ── Helpers ───────────────────────────────────────
  const selectedInvoice = invoices.find(i => i.invoiceNo === selectedInvoiceNo);

  // ── Auto-fill when invoice is chosen ───────────────
  useEffect(() => {
    if (selectedInvoice) {
      const due = (selectedInvoice.amount - (selectedInvoice.paid || 0)).toFixed(2);
      setAmount(due);
      setTransactionDescription(selectedInvoice.description || '');
      setSelectedGLAccount(selectedInvoice.glAccount || '');
    } else {
      setAmount('');
      setTransactionDescription('');
      setSelectedGLAccount('');
    }
  }, [selectedInvoice]);

  // ── Submit ────────────────────────────────────────
  const handleSubmit = async () => {
    if (submitting) return; // guard against double-submit (a 2nd POST would hit "Amount exceeds due")

    // basic required fields
    if (!learnerAdmNo || !selectedInvoiceNo || !amount || !paymentDate || !selectedBank || !reference.trim()) {
      setError('All required fields must be filled');
      return;
    }

    // over-payment guard
    if (selectedInvoice) {
      const due = selectedInvoice.amount - selectedInvoice.paid;
      if (parseFloat(amount) > due) {
        setError(`Cannot pay more than due (${sym()} ${fmt(due)})`);
        return;
      }
    }

    setSubmitting(true);
    try {
      const payload = {
        learnerAdmNo,
        invoiceNo: selectedInvoiceNo,
        amount: parseFloat(amount),
        paymentDate,
        bankAccountNo: selectedBank,               // <-- real bank
        reference: reference.toUpperCase().trim(), // <-- always caps
        transactionDescription,
        glAccount: selectedGLAccount               // <-- real GL
      };

      const { data } = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/finance/payments`,
        payload,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );

      setCreatedReceipt(data.receipt);
      setError('');
      setSelectedInvoiceNo('');
      setAmount('');
      setSelectedBank('');
      setReference('');
      setTransactionDescription('');
      setSelectedGLAccount('');
    } catch (err) {
      setError(err.response?.data?.error || 'Payment recording failed');
    } finally {
      setSubmitting(false);
    }
  };

  const printDate = createdReceipt
    ? new Date(createdReceipt.paymentDate).toLocaleDateString(locale(), { year: 'numeric', month: 'long', day: 'numeric' })
    : '';

  // ── UI ─────────────────────────────────────────────
  return (
    <>
      <style>{`
        @media print {
          body * { visibility: hidden; }
          #receipt-print-area, #receipt-print-area * { visibility: visible; }
          #receipt-print-area { position: absolute; left: 0; top: 0; width: 100%; padding: 40px; box-sizing: border-box; font-family: serif; }
        }
        @media screen { #receipt-print-area { display: none; } }
      `}</style>

      {createdReceipt && (
        <Box id="receipt-print-area">
          <CompanyHeader title="Payment Receipt" subtitle={createdReceipt.receiptNo} />
          <Box textAlign="center" mb={2}>
            <Typography variant="body2">
              Receipt No: <strong>{createdReceipt.receiptNo}</strong> &nbsp;·&nbsp; Date: {printDate}
            </Typography>
          </Box>
          <Box sx={{ border: '1px solid #ccc', p: 2, mb: 2 }}>
            <Typography variant="body2"><strong>Received From:</strong> {createdReceipt.learnerAdmNo}</Typography>
            <Typography variant="body2"><strong>For Invoice:</strong> {createdReceipt.invoiceNo}</Typography>
          </Box>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginBottom: 16 }}>
            <thead>
              <tr style={{ backgroundColor: '#f0f0f0' }}>
                <th style={{ border: '1px solid #ccc', padding: '6px 10px', textAlign: 'left' }}>Description</th>
                <th style={{ border: '1px solid #ccc', padding: '6px 10px', textAlign: 'left' }}>Reference</th>
                <th style={{ border: '1px solid #ccc', padding: '6px 10px', textAlign: 'right' }}>Amount ({sym()})</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ border: '1px solid #ccc', padding: '6px 10px' }}>{createdReceipt.transactionDescription}</td>
                <td style={{ border: '1px solid #ccc', padding: '6px 10px' }}>{createdReceipt.reference}</td>
                <td style={{ border: '1px solid #ccc', padding: '6px 10px', textAlign: 'right' }}>
                  {fmt(createdReceipt.amount)}
                </td>
              </tr>
              <tr style={{ fontWeight: 'bold', backgroundColor: '#f9f9f9' }}>
                <td colSpan={2} style={{ border: '1px solid #ccc', padding: '6px 10px', textAlign: 'right' }}>TOTAL RECEIVED</td>
                <td style={{ border: '1px solid #ccc', padding: '6px 10px', textAlign: 'right' }}>
                  {sym()} {fmt(createdReceipt.amount)}
                </td>
              </tr>
            </tbody>
          </table>
          <Typography variant="caption">Bank: {bankLabel(createdReceipt.bankAccountNo)}</Typography><br />
          <Typography variant="caption">GL Account: {glLabel(createdReceipt.glAccount)}</Typography>
          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption">Received by: _______________________</Typography>
            <Typography variant="caption">Authorized by: _______________________</Typography>
          </Box>
          <ReportFooter note="This is a system-generated receipt. Thank you for your payment." />
        </Box>
      )}

    <Box sx={{ maxWidth: 700, mx: 'auto', mt: 4 }}>
      <Card elevation={3}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Typography variant="h5" color="primary">Record Payment Receipt</Typography>
            <Box display="flex" gap={1}>
              <Button startIcon={<ArrowBack />} size="small" variant="outlined" onClick={() => navigate(-1)}>Back</Button>
              <Tooltip title="Dashboard">
                <IconButton onClick={() => navigate('/dashboard')} sx={{ bgcolor: 'background.paper' }}>
                  <Home />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {createdReceipt && (
            <Alert severity="success" sx={{ mb: 2 }}
              action={
                <Box display="flex" gap={1} flexWrap="wrap">
                  <Button size="small" startIcon={<Print />} variant="contained" onClick={handlePrint}>
                    Print Receipt
                  </Button>
                  <Button size="small" variant="outlined" onClick={() => { setCreatedReceipt(null); setLearnerAdmNo(''); }}>
                    New
                  </Button>
                  <Button size="small" onClick={() => navigate('/learners')}>Learners</Button>
                </Box>
              }>
              Receipt <strong>{createdReceipt.receiptNo}</strong> recorded successfully.
            </Alert>
          )}

          <Divider sx={{ mb: 2 }} />

          <Grid container spacing={2}>
            {/* Learner AdmNo */}
            <Grid item xs={12} md={6}>
              <TextField
                label="Learner AdmNo"
                value={learnerAdmNo}
                onChange={e => setLearnerAdmNo(e.target.value)}
                fullWidth
                disabled={!!state?.learnerAdmNo}
                helperText="Enter admission number"
              />
            </Grid>

            {/* Invoice (Due) */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required disabled={!learnerAdmNo || invoicesLoading}>
                <InputLabel>Invoice (Due)</InputLabel>
                <Select
                  value={selectedInvoiceNo}
                  onChange={e => {
                    const inv = invoices.find(i => i.invoiceNo === e.target.value);
                    const due = inv ? (inv.amount - inv.paid).toFixed(2) : '';
                    setSelectedInvoiceNo(e.target.value);
                    setAmount(due);
                    setTransactionDescription(inv?.description || '');
                    setSelectedGLAccount(inv?.glAccount || '');
                  }}
                  label="Invoice (Due)"
                >
                  {invoicesLoading ? (
                    <MenuItem disabled>Loading invoices…</MenuItem>
                  ) : invoices.length === 0 ? (
                    <MenuItem disabled>No unpaid invoices</MenuItem>
                  ) : (
                    invoices.map(inv => {
                      const due = inv.amount - (inv.paid || 0);
                      return (
                        <MenuItem key={inv.invoiceNo} value={inv.invoiceNo}>
                          {inv.invoiceNo} – {inv.description}{' '}
                          <Typography component="span"
                                      sx={{ ml: 1, fontWeight: 'bold', color: 'success.main' }}>
                            (Due: {sym()} {fmt(due)})
                          </Typography>
                        </MenuItem>
                      );
                    })
                  )}
                </Select>
                <FormHelperText>
                  {learnerAdmNo
                    ? invoicesLoading ? 'Loading…' : 'Select invoice to pay'
                    : 'Enter AdmNo first'}
                </FormHelperText>
              </FormControl>
            </Grid>

            {/* Amount Paying */}
            <Grid item xs={12} md={6}>
              <TextField
                label="Amount Paying"
                type="number"
                value={amount}
                onChange={e => setAmount(e.target.value)}
                fullWidth
                required
                inputProps={{ step: '0.01', min: '0.01' }}
                helperText={
                  selectedInvoice ? (
                    <span>
                      Due: <strong>{sym()} {fmt(selectedInvoice.amount - selectedInvoice.paid)}</strong>
                      {parseFloat(amount) > (selectedInvoice.amount - selectedInvoice.paid) && (
                        <span style={{ color: 'red' }}> (Exceeds due!)</span>
                      )}
                    </span>
                  ) : ''
                }
              />
            </Grid>

            {/* Payment Date */}
            <Grid item xs={12} md={6}>
              <TextField
                label="Payment Date"
                type="date"
                value={paymentDate}
                onChange={e => setPaymentDate(e.target.value)}
                fullWidth
                required
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* **Bank Account Dropdown** */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required disabled={banksLoading}>
                <InputLabel>Bank Account</InputLabel>
                <Select
                  value={selectedBank}
                  onChange={e => setSelectedBank(e.target.value)}
                  label="Bank Account"
                >
                  {banksLoading ? (
                    <MenuItem disabled>Loading banks…</MenuItem>
                  ) : banks.length === 0 ? (
                    <MenuItem disabled>No banks configured</MenuItem>
                  ) : (
                    banks.map(b => (
                      <MenuItem key={b.accountNo} value={b.accountNo}>
                        {b.bankName} – {b.accountName} ({b.accountNo})
                      </MenuItem>
                    ))
                  )}
                </Select>
                <FormHelperText>Select the receiving bank</FormHelperText>
              </FormControl>
            </Grid>

            {/* Reference – always UPPERCASE */}
            <Grid item xs={12} md={6}>
              <TextField
                label="Reference (e.g., M-PESA code)"
                value={reference}
                onChange={e => setReference(e.target.value.toUpperCase())}
                fullWidth
                required
                inputProps={{ style: { textTransform: 'uppercase' } }}
                helperText="Automatically converted to uppercase"
              />
            </Grid>

            {/* Transaction Description */}
            <Grid item xs={12}>
              <TextField
                label="Transaction Description"
                value={transactionDescription}
                onChange={e => setTransactionDescription(e.target.value)}
                fullWidth
                multiline
                rows={2}
              />
            </Grid>

            {/* **GL Account Dropdown** (auto-filled from bank) */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required disabled={glLoading}>
                <InputLabel>GL Account</InputLabel>
                <Select
                  value={selectedGLAccount}
                  onChange={e => setSelectedGLAccount(e.target.value)}
                  label="GL Account"
                >
                  {glLoading ? (
                    <MenuItem disabled>Loading GL accounts…</MenuItem>
                  ) : glAccounts.length === 0 ? (
                    <MenuItem disabled>No GL accounts</MenuItem>
                  ) : (
                    glAccountOptions(glAccounts, { includeNone: false })
                  )}
                </Select>
                <FormHelperText>
                  {selectedInvoice ? `Auto-filled from invoice (${selectedInvoice.glAccount || 'no GL on invoice'})` : 'Auto-filled when you select an invoice'}
                </FormHelperText>
              </FormControl>
            </Grid>

            {/* Submit */}
            <Grid item xs={12} sx={{ textAlign: 'center' }}>
              <Button variant="contained" onClick={handleSubmit} sx={{ mr: 2 }} disabled={submitting}>
                {submitting ? 'Recording…' : 'Record Payment'}
              </Button>
              <Button variant="outlined" onClick={() => navigate('/learners')} disabled={submitting}>
                Cancel
              </Button>
            </Grid>
          </Grid>

          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        </CardContent>
      </Card>
    </Box>
    </>
  );
};

export default PaymentReceiptForm;